import { ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { CustomTableReport } from 'src/app/interfaces/custom-table-report/custom-table-report.interface';
import { RentalReportFilterModel } from 'src/app/models/rental/rental-report-filter.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { ClientService } from 'src/app/services/archive/client.service';
import { CostCenterService } from 'src/app/services/archive/cost-center.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { InvoicingReportService } from 'src/app/services/invoicing/invoicing-report.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { RentalPeriodService } from 'src/app/services/rental/rental-period.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { CustomValidators } from 'src/app/validators/custom-validators';

@Component({
  selector: 'app-balances-cost-center',
  templateUrl: './balances-cost-center.component.html',
  styleUrls: ['./balances-cost-center.component.scss'],
  standalone: false
})
export class BalancesCostCenterComponent implements OnInit {

  private readonly articleService = inject(ArticleService);
  private readonly branchService = inject(BranchService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly clientService = inject(ClientService);
  private readonly costCenterService = inject(CostCenterService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly headerService = inject(HeaderService);
  private readonly rentalPeriodService = inject(RentalPeriodService);
  private readonly invoicingReportService = inject(InvoicingReportService);
  private readonly storeService = inject(StoreService);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('filtersContainer') filtersContainer!: ElementRef;

  changeFilters: boolean = false;
  dataReport: CustomTableReport;
  filterObserver?: ResizeObserver;
  filtersHeight: number = 0;
  filteredStores: any[] = [];
  formInvoicingReportFilter: FormGroup = new FormGroup({});
  listArticle: any[] = [];
  listBranch: any[] = [];
  listClient: any[] = [];
  listCostCenter: any[] = [];
  listStore: any[] = [];
  nameWindow: string = 'Informe de Saldos por C.Costos';
  projectName = ProjectName.projectName;

  async ngOnInit() {
    this.validateForm();
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getAllLists();
    await this.getFirstBranchByUserAuthenticated();
  }

  ngAfterViewInit() {
    this.filterObserver = new ResizeObserver(() => {
      this.filtersHeight = this.filtersContainer?.nativeElement?.offsetHeight || 0;
    });
    this.filterObserver.observe(this.filtersContainer.nativeElement);
  }

  ngOnDestroy() {
    this.filterObserver?.disconnect();
  }

  changeArticle(articleId: number | null) {
    if (articleId === 0 || articleId === null || articleId === undefined) {
      this.formInvoicingReportFilter.controls["articleId"].patchValue(0);
    }
    this.changeFilter();
  }

  async changeClient(clientId: number) {
    this.formInvoicingReportFilter.controls["costCenterId"].patchValue(0);
    this.formInvoicingReportFilter.controls["storeId"].patchValue(0);
    this.changeFilter();
    if (clientId && clientId !== 0) {
      await this.getAllLists('costCenters');
    } else {
      await this.getAllLists('costCenters');
      this.formInvoicingReportFilter.controls["clientId"].patchValue(0);
    }
  }

  changeCostCenter(costCenterId: number | null) {
    if (costCenterId === 0 || costCenterId === null || costCenterId === undefined) {
      this.formInvoicingReportFilter.controls["costCenterId"].patchValue(0);
    }
    this.changeFilter();
  }

  changeFilter() {
    this.changeFilters = false;
  }

  changeStore(storeId: number | null) {
    if (storeId === 0 || storeId === null || storeId === undefined) {
      this.formInvoicingReportFilter.controls["storeId"].patchValue(0);
    }
    this.changeFilter();
  }

  async clear() {
    this.changeFilter();
    this.validateForm();
    await this.getFirstBranchByUserAuthenticated();
    await this.getAllLists('costCenters');
  }

  async exportExcel() {
    if (this.changeFilters) {
      const dateInitial = this.formInvoicingReportFilter.controls['dateInitial'].value;
      const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
      const period = initialDate.slice(5);
      const year = initialDate.slice(0, -3);

      const modelRentalReportFilter = this.formInvoicingReportFilter.value as RentalReportFilterModel;
      modelRentalReportFilter.year = parseInt(year);
      modelRentalReportFilter.period = parseInt(period);
      try {
        const response = await firstValueFrom(this.invoicingReportService.exportInvoicingReportBalancesCostCenter(modelRentalReportFilter));
        const blob = response;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Informe de Saldos por C.Costos.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al exportar el informe.', error);
      }
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para exportar.'
      );
    }
  }

  async getAllLists(...listsToLoad: Array<'branches' | 'costCenters' | 'clients' | 'articles' | 'stores'>) {
    try {
      const lists: Record<string, () => Promise<void>> = {
        branches: async () => {
          this.listBranch = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync())
        },
        clients: async () => {
          this.listClient = await firstValueFrom(this.clientService.getAllClientsForSelectAsync())
        },
        costCenters: async () => {
          const currentClientId = this.formInvoicingReportFilter.get('clientId')?.value || 0;
          this.listCostCenter = await firstValueFrom(this.costCenterService.getAllCostCentersForSelectByClientAsync(currentClientId, 0))
        },
        articles: async () => {
          this.listArticle = await firstValueFrom(this.articleService.getAllArticlesForSelectAsync())
        },
        stores: async () => {
          this.listStore = await firstValueFrom(this.storeService.getAllStoresForSelectAsync())
        }
      };
      let tasks: Promise<void>[];
      if (listsToLoad.length > 0) {
        tasks = listsToLoad.map(key => lists[key]());
      } else {
        tasks = [
          lists.branches(),
          lists.clients(),
          lists.costCenters(),
          lists.articles(),
          lists.stores()
        ];
      }
      await Promise.all(tasks);
      return true;
    } catch (error) {
      console.error('Error al obtener las listas:', error);
      return false;
    }
  }

  async getFirstBranchByUserAuthenticated() {
    try {
      const branch = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      this.formInvoicingReportFilter.controls["branchId"].patchValue(branch.branchId);
      this.cdr.detectChanges();
      await this.getLastRentalPeriod(branch.branchId);
      this.filteredStores = this.listStore;
    } catch (error) {
      console.error("Error al consultar la sucursal:", error);
    }
  }

  async getLastRentalPeriod(branchId: number) {
    try {
      const rentalPeriod = await firstValueFrom(this.rentalPeriodService.getLastRentalPeriod(branchId));
      this.formInvoicingReportFilter.controls["dateInitial"].patchValue(moment(rentalPeriod.initialDate).startOf('month').toDate());
    } catch (error) {
      console.error("Error al consultar el periodo de alquiler.", error);
    }
  }

  printReport() {
    if (this.changeFilters) {
      window.print();
    } else {
      this.toastHandlerService.showToast(
        'info',
        'No se encontraron datos para imprimir.'
      );
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.formInvoicingReportFilter.valid) {
      this.showValidationMessages();
      return;
    }
    const dateInitial = this.formInvoicingReportFilter.get("dateInitial")?.value;
    const initialDate = `${dateInitial.getFullYear()}-${(dateInitial.getMonth() + 1).toString().padStart(2, '0')}`;
    const period = initialDate.slice(5);
    const year = initialDate.slice(0, -3);

    const modelRentalReportFilter = this.formInvoicingReportFilter.value as RentalReportFilterModel;
    modelRentalReportFilter.year = parseInt(year);
    modelRentalReportFilter.period = parseInt(period);

    try {
      this.dataReport = await firstValueFrom(this.invoicingReportService.invoicingBalancesCostcenter(modelRentalReportFilter));
      if (this.dataReport.data && this.dataReport.data.length != 0 && this.dataReport.data.length != null) {
        this.changeFilters = true;
      } else {
        this.changeFilters = false;
        this.toastHandlerService.showToast(
          'info',
          'No se encontraron datos.'
        );
      }
    } catch (error) {
      console.error("Error al consultar el informe.", error);
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      dateInitial: {
        required: 'El campo Período es obligatorio.'
      },
      branchId: {
        notNumber: 'Debe seleccionar un Sucursal válida',
      },
      clientId: {
        notNumber: 'Debe seleccionar un Cliente válido.'
      },
      costCenterId: {
        notNumber: 'Debe seleccionar un Centro de Costos válido.'
      },
      articleId: {
        notNumber: 'Debe seleccionar un Artículo válido.'
      },
      storeId: {
        notNumber: 'Debe seleccionar una Bodega válida.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formInvoicingReportFilter.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateForm() {
    this.formInvoicingReportFilter = this.formBuilder.group({
      dateInitial: ['', [Validators.required]],
      branchId: [0, [CustomValidators.numberOnlyValidator()]],
      clientId: [0, [CustomValidators.numberOnlyValidator()]],
      costCenterId: [0, [CustomValidators.numberOnlyValidator()]],
      articleId: [0, [CustomValidators.numberOnlyValidator()]],
      storeId: [0, [CustomValidators.numberOnlyValidator()]],
      articleName: [""]
    });
  }

  validateFormControls() {
    Object.keys(this.formInvoicingReportFilter.controls).forEach((key) => {
      const control = this.formInvoicingReportFilter.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }
}
